import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import NightlightRoundIcon from '@mui/icons-material/NightlightRound';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import { useAuthState } from 'react-firebase-hooks/auth';
import SimpleNotesInput from '../UI/SimpleNotesInput';
import LogFormShell from '../UI/LogFormShell';
import LogTimeField from '../UI/LogTimeField';
import { auth } from '../../services/firebase';
import useChildName from '../../hooks/useChildName';
import useActiveSleep from '../../hooks/useActiveSleep';
import {
  inferSleepTypeFromDateTime,
  inferSleepTypeFromWindow,
  normalizeSleepType,
} from '../../services/activeSleepService';
import colors from '../../assets/theme/colors';
import { getAdaptiveSleepLogConfig } from '../Dashboard/shared/adaptiveSleepLogConfig';

const getDefaultSleepMode = () => 'night';
const normalizeType = (value = 'night') => normalizeSleepType(value);

const getSleepDatesForMode = (mode, referenceDate = new Date()) => {
  const base = new Date(referenceDate);
  base.setHours(0, 0, 0, 0);

  if (mode === 'nap') {
    return { bedDate: base, wakeDate: base };
  }

  const bedDate = new Date(base);
  return { bedDate, wakeDate: new Date(base) };
};

const getCurrentTimeValue = () => {
  const now = new Date();
  return [
    String(now.getHours()).padStart(2, '0'),
    String(now.getMinutes()).padStart(2, '0'),
  ].join(':');
};

const parseTime = (timeValue) => {
  if (!timeValue) return null;
  const [hours, minutes] = String(timeValue).split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return { hours, minutes };
};

const coerceDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value?.toDate === 'function') {
    const resolved = value.toDate();
    return resolved instanceof Date && !Number.isNaN(resolved.getTime()) ? resolved : null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatShortDate = (date) => {
  const resolved = coerceDateValue(date);
  if (!resolved) return '';
  return resolved.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatLongDate = (date) => {
  const resolved = coerceDateValue(date);
  if (!resolved) return '';
  return resolved.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTimeLabel = (value) => {
  const date = coerceDateValue(value);
  if (date) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  const time = parseTime(value);
  if (!time) return '';

  const resolved = new Date();
  resolved.setHours(time.hours, time.minutes, 0, 0);
  return resolved.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const combineDateAndTime = (dateValue, timeValue) => {
  const baseDate = coerceDateValue(dateValue);
  const time = parseTime(timeValue);
  if (!baseDate || !time) {
    return null;
  }

  const result = new Date(baseDate);
  result.setHours(time.hours, time.minutes, 0, 0);
  return result;
};

const addCalendarDay = (dateValue, days = 1) => {
  const date = coerceDateValue(dateValue);
  if (!date) {
    return null;
  }

  const resolved = new Date(date);
  resolved.setDate(resolved.getDate() + days);
  return resolved;
};

const resolveSleepStartAt = (bedDate, bedtime) => {
  return combineDateAndTime(bedDate, bedtime) || null;
};

const resolveSleepEndAt = (startAt, wakeDate, wakeTime) => {
  if (!wakeTime) {
    return null;
  }

  const resolvedStart = coerceDateValue(startAt);
  const initialWakeAt = combineDateAndTime(wakeDate, wakeTime);
  if (!initialWakeAt) {
    return null;
  }

  if (resolvedStart && initialWakeAt <= resolvedStart) {
    return addCalendarDay(initialWakeAt, 1) || initialWakeAt;
  }

  return initialWakeAt;
};

const formatDurationLabel = (durationMin) => {
  if (durationMin == null || Number.isNaN(Number(durationMin))) {
    return '';
  }

  const minutes = Math.max(0, Math.round(Number(durationMin)));
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;

  if (hours <= 0) {
    return `${remainder}m`;
  }

  return `${hours}h ${remainder}m`;
};

const getEntryContextText = (entry = {}) => String(
  entry.note
  || entry.notes
  || entry.sourceText
  || entry.originalData?.sourceText
  || entry.source?.body
  || entry.originalData?.source?.body
  || entry.content
  || entry.originalData?.content
  || entry.text
  || entry.originalData?.text
  || entry.primaryText
  || entry.originalData?.primaryText
  || entry.label
  || entry.originalData?.label
  || entry.titlePrefix
  || entry.originalData?.titlePrefix
  || entry.title
  || entry.originalData?.title
  || ''
).trim();

const formatRecordedSleepLabel = (startAt, endAt, durationMin, status, sleepType = 'night') => {
  const start = coerceDateValue(startAt);
  const end = coerceDateValue(endAt);
  const startDateLabel = formatLongDate(start);
  const endDateLabel = formatLongDate(end);
  const startTimeLabel = start ? formatTimeLabel(start) : '';
  const endTimeLabel = end ? formatTimeLabel(end) : '';
  const isNap = normalizeType(sleepType) === 'nap';

  if (status === 'in_progress' && !end) {
    return `${isNap ? 'Nap' : 'Sleep'} started ${startDateLabel} at ${startTimeLabel || getCurrentTimeValue()} · in progress`;
  }

  if (!start || !end) {
    return `${isNap ? 'Nap' : 'Sleep'} recorded`;
  }

  if (isNap && start.toDateString() === end.toDateString()) {
    return `Napped ${startDateLabel || 'today'} at ${startTimeLabel || getCurrentTimeValue()} to ${endTimeLabel || getCurrentTimeValue()} · ${formatDurationLabel(durationMin) || 'in progress'}`;
  }

  return `Slept ${startDateLabel || 'earlier'} at ${startTimeLabel || getCurrentTimeValue()} → ${endDateLabel || 'today'} at ${endTimeLabel || getCurrentTimeValue()} · ${formatDurationLabel(durationMin) || 'in progress'}`;
};

const formatSleepHeaderDateLabel = (startDate, endDate = null) => {
  const start = coerceDateValue(startDate);
  const end = coerceDateValue(endDate);

  if (!start) {
    return '';
  }

  const startLabel = formatShortDate(start);
  if (!end) {
    return startLabel;
  }

  return start.toDateString() === end.toDateString()
    ? startLabel
    : `${startLabel} → ${formatShortDate(end)}`;
};

const clearSleepDraft = (childId) => {
  if (typeof window === 'undefined' || !childId) {
    return;
  }

  const storageKey = childId ? `captureez:sleep-draft:${childId}` : null;
  if (!storageKey) return;

  try {
    window.localStorage.removeItem(storageKey);
  } catch (error) {
    // Ignore storage errors.
  }
};

const buildSleepWindow = ({ bedDate, wakeDate, bedtime, wakeTime }) => {
  const start = resolveSleepStartAt(bedDate, bedtime);
  if (!start) {
    return {
      start: null,
      end: null,
      durationMin: null,
      durationHours: null,
      status: wakeTime ? 'complete' : 'in_progress',
      timestamp: coerceDateValue(wakeDate) || new Date(),
      displayDate: coerceDateValue(wakeDate) || new Date(),
    };
  }

  const hasWakeTime = Boolean(wakeTime);
  const end = hasWakeTime ? resolveSleepEndAt(start, wakeDate, wakeTime) : null;

  const durationMin = end ? Math.max(0, Math.round((end - start) / 60000)) : null;
  const durationHours = durationMin != null ? Number((durationMin / 60).toFixed(1)) : null;
  const displayDate = coerceDateValue(wakeDate) || new Date(start);
  const timestamp = end || (() => {
    const anchor = new Date(displayDate);
    anchor.setHours(8, 0, 0, 0);
    return anchor;
  })();

  return {
    start,
    end,
    durationMin,
    durationHours,
    status: hasWakeTime ? 'complete' : 'in_progress',
    timestamp,
    displayDate,
  };
};

const SleepLogSheet = ({ open, onClose, child, mode = 'create', initialData = null, sleepType = 'night' }) => {
  const [user] = useAuthState(auth);
  const currentUser = user || auth.currentUser;
  const { childName } = useChildName(child?.id);
  const {
    activeSleep,
    startSleep: startActiveSleep,
    updateActiveSleep: updateActiveSleepSession,
    endSleep: endActiveSleep,
    discardSleep: discardActiveSleep,
  } = useActiveSleep(child?.id);
  const [sleepMode, setSleepMode] = useState(normalizeType(sleepType));
  const [bedDate, setBedDate] = useState(() => getSleepDatesForMode(getDefaultSleepMode()).bedDate);
  const [wakeDate, setWakeDate] = useState(() => getSleepDatesForMode(getDefaultSleepMode()).wakeDate);
  const [bedtime, setBedtime] = useState(getCurrentTimeValue());
  const [wakeTime, setWakeTime] = useState('');
  const [disturbance, setDisturbance] = useState('none');
  const [sleepQuality, setSleepQuality] = useState('fair');
  const [notesData, setNotesData] = useState({ text: '', mediaFile: null, audioBlob: null });
  const [notesClearToken, setNotesClearToken] = useState(0);
  const [saving, setSaving] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const autoSaveTimerRef = useRef(null);
  const autoSaveSignatureRef = useRef('');
  const sleepConfig = useMemo(() => getAdaptiveSleepLogConfig(child), [child]);

  useEffect(() => {
    if (typeof console === 'undefined') {
      return;
    }

    console.info('[sleep] SleepLogSheet child context', {
      childId: child?.id || null,
      childName: child?.name || childName || null,
      open,
      mode,
      sleepType,
    });
  }, [child?.id, child?.name, childName, mode, open, sleepType]);

  const clearTimers = React.useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  }, []);

  const hydrateFromEntry = React.useCallback((entry) => {
    const sleepDetails = entry?.sleepDetails || entry?.originalData?.sleepDetails || entry?.data?.sleepDetails || {};
    const nextMode = normalizeType(sleepDetails.type || sleepDetails.mode || 'night');
    const startAt = coerceDateValue(
      sleepDetails.startAt
      || sleepDetails.startTime
      || sleepDetails.bedtime
      || entry?.startAt
      || entry?.startTime
      || entry?.bedtime
      || entry?.bedtimeClock
    );
    const endAt = coerceDateValue(
      sleepDetails.endAt
      || sleepDetails.endTime
      || sleepDetails.wakeTime
      || entry?.endAt
      || entry?.endTime
      || entry?.wakeTime
      || entry?.wakeTimeClock
    );
    const bedAnchor = coerceDateValue(
      sleepDetails.anchorDate
      || sleepDetails.bedDate
      || sleepDetails.localDate
      || startAt
      || entry?.startAt
      || entry?.startTime
      || entry?.displayDate
      || entry?.entryDate
      || entry?.timestamp
      || entry?.originalData?.timestamp
    );
    const wakeAnchor = coerceDateValue(
      sleepDetails.wakeDate
      || endAt
      || sleepDetails.displayDate
      || entry?.displayDate
      || entry?.entryDate
      || entry?.timestamp
      || entry?.originalData?.timestamp
    );
    const defaults = getSleepDatesForMode(nextMode, bedAnchor || startAt || new Date());

    setSleepMode(nextMode);
    setBedDate(bedAnchor || defaults.bedDate);
    setWakeDate(wakeAnchor || (endAt ? new Date(endAt) : defaults.wakeDate));
    setBedtime(
      sleepDetails.bedtimeClock
      || sleepDetails.bedtime
      || entry?.bedtimeClock
      || entry?.bedtime
      || (startAt ? startAt.toTimeString().slice(0, 5) : getCurrentTimeValue())
    );
    setWakeTime(
      sleepDetails.wakeTimeClock
      || sleepDetails.wakeTime
      || entry?.wakeTimeClock
      || entry?.wakeTime
      || (endAt ? endAt.toTimeString().slice(0, 5) : '')
    );
    setDisturbance(sleepDetails.disturbances || sleepDetails.disturbance || entry?.disturbance || 'none');
    setSleepQuality(sleepDetails.quality || entry?.sleepQuality || 'fair');
    setNotesData({
      text: sleepDetails.notes
        || entry?.notes
        || entry?.originalData?.notes
        || entry?.note
        || getEntryContextText(entry)
        || '',
      mediaFile: null,
      audioBlob: null,
    });
    setNotesClearToken((token) => token + 1);
  }, []);

  const resetForm = React.useCallback((mode = getDefaultSleepMode()) => {
    const defaults = getSleepDatesForMode(mode);
    setSleepMode(mode);
    setBedDate(defaults.bedDate);
    setWakeDate(defaults.wakeDate);
    setBedtime(getCurrentTimeValue());
    setWakeTime('');
    setDisturbance('none');
    setSleepQuality('fair');
    setNotesData({ text: '', mediaFile: null, audioBlob: null });
    setNotesClearToken((token) => token + 1);
  }, []);

  const activeSession = activeSleep;

  useEffect(() => {
    if (!open) {
      clearTimers();
      return undefined;
    }

    const editEntry = mode === 'edit' ? (initialData || null) : null;
    if (editEntry) {
      hydrateFromEntry(editEntry);
    } else if (activeSession) {
      hydrateFromEntry({
        ...activeSession,
        sleepDetails: {
          type: activeSession.type || sleepMode,
          startTime: activeSession.startTime,
          startAt: activeSession.startAt,
          endAt: activeSession.endAt,
          endTime: activeSession.endTime,
          notes: activeSession.notes || '',
          quality: activeSession.quality || '',
          disturbances: activeSession.disturbances || '',
        },
      });
    } else {
      resetForm(getDefaultSleepMode());
    }

    return () => {
      clearTimers();
    };
  }, [activeSession, child?.id, clearTimers, hydrateFromEntry, initialData, mode, open, resetForm, sleepMode]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  const sleepWindow = useMemo(() => buildSleepWindow({
    bedDate,
    wakeDate,
    bedtime,
    wakeTime,
  }), [bedDate, bedtime, wakeDate, wakeTime]);
  const bedtimePreview = combineDateAndTime(bedDate, bedtime);
  const wakeTimePreview = wakeTime ? combineDateAndTime(wakeDate, wakeTime) : null;
  const isWakeTimeInvalid = Boolean(bedtimePreview && wakeTimePreview && wakeTimePreview <= bedtimePreview);
  const sleepDateLabel = formatSleepHeaderDateLabel(sleepWindow.start || bedDate, sleepWindow.end || null);

  const resolvedDisplayedSession = useMemo(() => {
    if (activeSleep?.id) {
      return {
        id: activeSleep.id,
        childId: activeSleep.childId || child?.id || null,
        type: normalizeType(activeSleep.type || sleepMode),
        startTime: activeSleep.startTime || activeSleep.startAt || null,
        startAt: activeSleep.startAt || activeSleep.startTime || null,
        endTime: activeSleep.endTime || activeSleep.endAt || null,
        endAt: activeSleep.endAt || activeSleep.endTime || null,
        status: String(activeSleep.status || 'active').toLowerCase(),
        source: 'hook',
      };
    }

    return null;
  }, [
    activeSleep,
    child?.id,
  ]);
  const resolvedIsActiveSession = Boolean(
    resolvedDisplayedSession?.startTime
    && !resolvedDisplayedSession?.endTime
    && String(resolvedDisplayedSession?.status || '').toLowerCase() === 'active'
  );
  const sheetTitle = mode === 'edit'
    ? sleepConfig.editTitle
    : (resolvedIsActiveSession ? sleepConfig.activeTitle : sleepConfig.startTitle);
  const sheetSubtitle = mode === 'edit'
    ? sleepConfig.editSubtitle
    : (resolvedIsActiveSession
      ? sleepConfig.activeSubtitle
      : sleepConfig.startSubtitle);
  const hasWakeTime = Boolean(resolvedDisplayedSession?.endTime);
  const showInProgressReminder = resolvedIsActiveSession;
  const sleepReminderText = resolvedIsActiveSession
    ? sleepConfig.activeStatusLabel
    : sleepConfig.readyStatusLabel;
  const sessionForActions = resolvedDisplayedSession || activeSession;

  useEffect(() => {
    if (!open) {
      autoSaveSignatureRef.current = '';
      return undefined;
    }

    if (mode === 'edit' || !child?.id || !currentUser?.uid || !resolvedIsActiveSession || !sessionForActions?.id) {
      return undefined;
    }

    const bedtimeDateTime = resolveSleepStartAt(bedDate, bedtime);
    if (!bedtimeDateTime) {
      return undefined;
    }

    const sessionType = normalizeType(sessionForActions?.type || inferSleepTypeFromDateTime(bedtimeDateTime));
    const payload = {
      notes: String(notesData?.text || '').trim(),
      quality: sleepQuality,
      disturbances: disturbance,
      startAt: bedtimeDateTime.toISOString(),
      startTime: bedtimeDateTime.toISOString(),
      endAt: null,
      endTime: null,
    };
    if (typeof console !== 'undefined') {
      console.info('[sleep] SleepLogSheet auto-save payload', {
        childId: child?.id || null,
        childName: child?.name || childName || null,
        mode,
        sleepType: sessionType,
        path: `activeSleepSessions/${child?.id || 'unknown'}_activeSleep`,
        payload,
        hasActiveSession: Boolean(sessionForActions?.id),
      });
    }
    const nextSignature = JSON.stringify({
      type: sessionType,
      ...payload,
    });

    if (autoSaveSignatureRef.current === nextSignature) {
      return undefined;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = window.setTimeout(async () => {
      try {
        await updateActiveSleepSession(sessionType, payload);
        autoSaveSignatureRef.current = nextSignature;
      } catch (error) {
        console.error('Error auto-saving active sleep session:', error);
      }
    }, 350);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [
    bedDate,
    bedtime,
    child?.id,
    disturbance,
    mode,
    notesData?.text,
    open,
    currentUser?.uid,
    sleepQuality,
    sessionForActions?.id,
    sessionForActions?.type,
    updateActiveSleepSession,
  ]);

  const handleEndSleep = async () => {
    if (!child?.id || !sessionForActions?.id) {
      return;
    }

    if (!wakeTime) {
      setConfirmationMessage(sleepConfig.addEndTimePrompt);
      setConfirmationOpen(true);
      return;
    }

    const noteText = String(notesData?.text || '').trim();
    const bedtimeDateTime = resolveSleepStartAt(bedDate, bedtime)
      || coerceDateValue(sessionForActions?.startAt)
      || coerceDateValue(sessionForActions?.startTime)
      || new Date();
    const resolvedWakeTime = resolveSleepEndAt(bedtimeDateTime, wakeDate, wakeTime);
    const endTime = resolvedWakeTime || new Date();
    const resolvedType = normalizeType(sessionForActions?.type || inferSleepTypeFromWindow(bedtimeDateTime, endTime));

    setSaving(true);
    try {
      await endActiveSleep(resolvedType, {
        endAt: endTime.toISOString(),
        endTime: endTime.toISOString(),
        quality: sleepQuality,
        disturbances: disturbance,
        notes: noteText,
        title: sleepConfig.savedTitle,
        titlePrefix: sleepConfig.savedTitlePrefix,
        text: noteText || sleepConfig.savedFallbackText,
      });

      clearSleepDraft(child.id);
      clearTimers();
      setConfirmationMessage(`${sleepConfig.endedMessagePrefix} ${formatRecordedSleepLabel(bedtimeDateTime, endTime, Math.max(0, Math.round((endTime - bedtimeDateTime) / 60000)), 'complete', resolvedType)}`);
      setConfirmationOpen(true);
      setNotesClearToken((token) => token + 1);
      onClose?.();
    } catch (error) {
      console.error('Error ending sleep log:', error);
      const errorCode = String(error?.code || '').toLowerCase();
      setConfirmationMessage(
        errorCode.includes('permission')
          ? 'Unable to end sleep because the app does not have permission yet. Refresh and try again.'
          : (error?.message || 'Unable to end sleep.')
      );
      setConfirmationOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleStartSleep = async () => {
    if (!child?.id || !currentUser?.uid || resolvedIsActiveSession) {
      return;
    }

    const bedtimeDateTime = resolveSleepStartAt(bedDate, bedtime);
    if (!bedtimeDateTime) {
      setConfirmationMessage(sleepConfig.startTimePrompt);
      setConfirmationOpen(true);
      return;
    }

    const sessionType = normalizeType(inferSleepTypeFromDateTime(bedtimeDateTime));
    const noteText = String(notesData?.text || '').trim();
    const payload = {
      notes: noteText,
      quality: sleepQuality,
      disturbances: disturbance,
      startAt: bedtimeDateTime.toISOString(),
      startTime: bedtimeDateTime.toISOString(),
      endAt: null,
      endTime: null,
      title: sleepConfig.savedTitle,
      titlePrefix: sleepConfig.savedTitlePrefix,
    };

    setSaving(true);
    try {
      await startActiveSleep(sessionType, payload);
      setConfirmationMessage(sleepConfig.startedMessage);
      setConfirmationOpen(true);
      onClose?.();
    } catch (error) {
      console.error('Error starting sleep log:', error);
      const errorCode = String(error?.code || '').toLowerCase();
      setConfirmationMessage(
        errorCode.includes('permission')
          ? 'Unable to start sleep because the app does not have permission yet. Refresh and try again.'
          : (error?.message || 'Unable to start sleep.')
      );
      setConfirmationOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardSleep = async () => {
    if (!child?.id) {
      onClose?.();
      return;
    }

    setSaving(true);
    try {
      await discardActiveSleep(normalizeType(sessionForActions?.type || sleepMode));
      clearSleepDraft(child.id);
      clearTimers();
      setNotesClearToken((token) => token + 1);
      onClose?.();
    } catch (error) {
      console.error('Error discarding sleep session:', error);
      setConfirmationMessage(error?.message || 'Unable to discard sleep.');
      setConfirmationOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const formBody = (
    <Stack spacing={2.25}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '18px',
          border: `1.5px solid ${colors.semantic.sleep.border}`,
          overflow: 'hidden',
          bgcolor: '#FFFFFF',
        }}
      >
        <Box
          sx={{
            px: 1.75,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            bgcolor: alpha(colors.semantic.sleep.bg, 0.33),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <NightlightRoundIcon sx={{ fontSize: 18, color: colors.semantic.sleep.ink }} />
            <Typography
              sx={{
                fontSize: '0.82rem',
                fontWeight: 650,
                color: colors.semantic.sleep.ink,
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {sleepDateLabel}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexShrink: 0 }}>
            {showInProgressReminder ? (
              <>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '999px',
                    bgcolor: colors.semantic.warning,
                    animation: 'sleepPulse 1.3s ease-in-out infinite',
                    '@keyframes sleepPulse': {
                      '0%': { transform: 'scale(1)', opacity: 0.8 },
                      '50%': { transform: 'scale(1.25)', opacity: 1 },
                      '100%': { transform: 'scale(1)', opacity: 0.8 },
                    },
                  }}
                />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: colors.semantic.sleep.ink }}>
                  {sleepConfig.activeStatusLabel}
                </Typography>
              </>
            ) : (
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: colors.semantic.sleep.ink }}>
                {sleepReminderText || 'Add wake time when sleep ends.'}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.1, p: 1.4 }}>
          <Box>
            <LogTimeField
              label={sleepConfig.startTimeLabel}
              value={bedtime || getCurrentTimeValue()}
              onChange={(event) => setBedtime(event.target.value)}
              icon={NightlightRoundIcon}
            />
            <Typography sx={{ mt: 0.65, fontSize: '0.72rem', color: colors.semantic.ink2 }}>
              {formatShortDate(bedDate)}
            </Typography>
          </Box>

          <Box>
            <LogTimeField
              label={sleepConfig.endTimeLabel}
              value={wakeTime}
              onChange={(event) => setWakeTime(event.target.value)}
              icon={WbSunnyRoundedIcon}
              sx={isWakeTimeInvalid ? { borderColor: colors.semantic.error } : {}}
              valueSx={isWakeTimeInvalid ? { color: colors.semantic.error } : {}}
              labelSx={isWakeTimeInvalid ? { color: colors.semantic.error } : {}}
              placeholder={resolvedIsActiveSession ? sleepConfig.addEndTimeText : sleepConfig.endTimeNotSetText}
            />
            <Typography sx={{ mt: 0.65, fontSize: '0.72rem', color: isWakeTimeInvalid ? colors.semantic.error : colors.semantic.ink2 }}>
              {isWakeTimeInvalid
                ? `${sleepConfig.endTimeLabel} must be after the ${sleepConfig.startTimeLabel.toLowerCase()}.`
                : wakeTime
                  ? formatShortDate(sleepWindow.end || wakeDate)
                  : (resolvedIsActiveSession ? sleepConfig.addEndTimeText : sleepConfig.endTimeNotSetText)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {hasWakeTime ? (
        <Box
          sx={{
            p: 1.75,
            borderRadius: '16px',
            bgcolor: alpha(colors.semantic.sleep.bg, 0.23),
            border: `1px solid ${alpha(colors.semantic.sleep.bg, 0.45)}`,
          }}
        >
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: colors.semantic.sleep.ink, mb: 1 }}>
            {sleepConfig.eventSectionLabel}
          </Typography>
          <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75}>
            {sleepConfig.eventOptions.map((option) => {
              const selected = disturbance === option.value;
              const IconComponent = option.icon;

              return (
                <Chip
                  key={option.value}
                  onClick={() => setDisturbance(option.value)}
                  icon={<IconComponent sx={{ fontSize: 13, color: selected ? '#FFFFFF' : colors.semantic.ink2 }} />}
                  label={option.label}
                  variant="outlined"
                  sx={{
                    height: 34,
                    px: 0.4,
                    borderRadius: 999,
                    bgcolor: selected ? colors.semantic.sleep.ink : '#FFFFFF',
                    borderColor: selected ? colors.semantic.sleep.ink : colors.semantic.sleep.border,
                    color: selected ? '#FFFFFF' : colors.semantic.ink2,
                    fontSize: '0.76rem',
                    fontWeight: 650,
                    '& .MuiChip-label': {
                      px: 0.25,
                    },
                    '& .MuiChip-icon': {
                      ml: 0.5,
                      mr: -0.25,
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Box>
      ) : null}

      {hasWakeTime ? (
        <Box>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: colors.semantic.ink, mb: 1 }}>
            {sleepConfig.qualitySectionLabel}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
              gap: 0.75,
            }}
          >
            {sleepConfig.qualityOptions.map((option) => {
              const selected = sleepQuality === option.value;
              const token = colors.semantic.sleepQuality[option.value] || colors.semantic.sleepQuality.fair;
              return (
                <ButtonBase
                  key={option.value}
                  onClick={() => setSleepQuality(option.value)}
                  sx={{
                    minHeight: 82,
                    borderRadius: '12px',
                    p: 0.45,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 0.35,
                    bgcolor: selected ? alpha(token.bg, 0.20) : '#FFFFFF',
                    border: `1.5px solid ${selected ? token.bg : colors.semantic.sleep.border}`,
                    color: selected ? token.ink : colors.semantic.ink2,
                    transition: 'background-color 180ms ease, border-color 180ms ease, transform 180ms ease',
                    '&:active': {
                      transform: 'scale(0.995)',
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      lineHeight: 1,
                      display: 'grid',
                      placeItems: 'center',
                      opacity: selected ? 1 : 0.75,
                    }}
                  >
                    {option.iconComponent ? <option.iconComponent sx={{ fontSize: 18 }} /> : null}
                  </Box>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.68rem',
                      lineHeight: 1.05,
                      fontWeight: selected ? 700 : 600,
                      color: selected ? token.ink : colors.semantic.ink2,
                    }}
                  >
                    {option.label}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      ) : null}

      <Box>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: colors.semantic.ink, mb: 1 }}>
          {sleepConfig.notesLabel}
        </Typography>
        <SimpleNotesInput
          value={notesData}
          onDataChange={setNotesData}
          clearData={notesClearToken}
          placeholder={sleepConfig.notesPlaceholder}
          hintText="Tip: use your phone mic to dictate notes."
          minRows={4}
          textFieldSx={{
            '& .MuiInputBase-root': {
              minHeight: 60,
              borderRadius: '14px',
              border: `1.5px solid ${colors.semantic.sleep.border}`,
              boxShadow: 'none',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
          }}
        />
      </Box>

      <Snackbar
        open={confirmationOpen}
        autoHideDuration={4500}
        onClose={() => setConfirmationOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setConfirmationOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {confirmationMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );

  const footerActions = (
    <>
      <Divider sx={{ my: 2.5 }} />
      <Box sx={{ display: 'flex', gap: 1.25 }}>
        <Button
          type="button"
          variant="outlined"
          onClick={resolvedIsActiveSession ? handleDiscardSleep : onClose}
          sx={{
            flex: 1,
            height: 50,
            borderRadius: '999px',
            borderColor: colors.semantic.line,
            color: colors.semantic.ink2,
            bgcolor: '#FFFFFF',
            fontWeight: 650,
            fontSize: '0.95rem',
            '&:hover': {
              bgcolor: '#FFFFFF',
              borderColor: colors.semantic.line,
            },
          }}
        >
          {resolvedIsActiveSession ? sleepConfig.discardButtonLabel : sleepConfig.cancelButtonLabel}
        </Button>
      </Box>
      {!resolvedIsActiveSession && mode !== 'edit' ? (
        <Button
          type="button"
          variant="outlined"
          onClick={handleStartSleep}
          disabled={saving}
          sx={{
            mt: 1,
            width: '100%',
            height: 48,
            borderRadius: '999px',
            borderColor: colors.semantic.sleep.border,
            color: colors.semantic.sleep.ink,
            bgcolor: '#FFFFFF',
            fontWeight: 700,
            fontSize: '0.94rem',
          }}
        >
          {sleepConfig.startButtonLabel}
        </Button>
      ) : null}
      {resolvedIsActiveSession ? (
        <Button
          type="button"
          variant="outlined"
          onClick={handleEndSleep}
          disabled={saving || !resolvedDisplayedSession?.id}
          sx={{
            mt: 1,
            width: '100%',
            height: 48,
            borderRadius: '999px',
            borderColor: colors.semantic.sleep.border,
            color: colors.semantic.sleep.ink,
            bgcolor: '#FFFFFF',
            fontWeight: 700,
            fontSize: '0.94rem',
          }}
          >
          {sleepConfig.endButtonLabel}
        </Button>
      ) : null}
    </>
  );

  return (
    <LogFormShell
      open={open}
      onClose={onClose}
      title={sheetTitle}
      titleBadge={childName || child?.name || 'Child'}
      compactTitle
      subtitle={sheetSubtitle}
      footer={footerActions}
      mobileBreakpoint="md"
      maxWidth="sm"
      surfaceSx={{
        backgroundColor: colors.semantic.bg,
      }}
      bodySx={{
        background: `linear-gradient(180deg, ${alpha(colors.semantic.sleep.bg, 0.06)} 0%, #FFFFFF 44%)`,
      }}
    >
      {formBody}
    </LogFormShell>
  );
};

export default SleepLogSheet;
